import { createAsyncThunk } from "@reduxjs/toolkit";

interface LoginResponse {
  message: string;
  user: {
    id: number;
    username: string;
    role: "admin" | "user";
  };
}

interface LogoutResponse {
  message: string;
}

interface SessionResponse {
  isAuthenticated: boolean;
  user: {
    id: number;
    username: string;
    role: "admin" | "user";
  };
}

export const loginUser = createAsyncThunk<
  LoginResponse, // returns the full response
  { username: string; password: string },
  { rejectValue: string }
>("auth/login", async (creds, { rejectWithValue }) => {
  try {
    const res = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/auth/login`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(creds),
    });
    const data = await res.json();

    console.log(data);

    // Need to send proper error message when password is incorrect
    if (!res.ok) {
      if (res.status === 401) {
        throw new Error('Invalid username or password');
      } else if (res.status === 500) {
        throw new Error('Server error. Please try again later.');
      } else if (data.message) {
        throw new Error(data.message);
      } else {
        throw new Error('An unexpected error occurred. Please try again.');
      }
    }
    
    return data as LoginResponse;
  } catch (e: any) {
    if (e.name === 'TypeError' && e.message.includes('fetch')) {
      return rejectWithValue('Network error. Please check your connection.');
    }

    console.log(e);
    return rejectWithValue(e.message || 'An unexpected error occurred. Please try again.');
  }
});

export const logoutUser = createAsyncThunk<
  LogoutResponse, // returns the full response
  void,
  { rejectValue: string }
>("auth/logout", async (_, { rejectWithValue }) => {
  try {
    const res = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/auth/logout`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    });
    const data = await res.json();
    return data as LogoutResponse;
  } catch (e: any) {
    return rejectWithValue(e.message || 'An unexpected error occurred. Please try again.');
  }
});

// authSlice.ts
export const checkSession = createAsyncThunk<
  SessionResponse,
  void,
  { rejectValue: string }
>(
  'auth/checkSession',
  async (_, { rejectWithValue }) => {
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/auth/session`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!res.ok) {
        throw new Error('Session check failed');
      }
      
      const data = await res.json();
      return data;
    } catch {
      return rejectWithValue("Session check failed");                     // treat as not authenticated
    }
  }
);